import Image from 'next/image';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { ColorTheme, Colorways } from 'types/constants';
import { cn, getSectionProps } from 'utils/styles';
import HeroBentoCta from './HeroBentoCta';
import HeroBentoLogo from './HeroBentoLogo';
import HeroBentoMedia from './HeroBentoMedia';
import type { HeroBentoCardProps, HeroBentoProps } from './types';

const getThemeColorways = (colorTheme?: ColorTheme) => {
  if (colorTheme === ColorTheme.PrimaryDark) {
    return {
      section: Colorways.Inverse,
      cta: Colorways.Nickel,
      logo: Colorways.ActionPrimary,
    };
  }
  return {
    section: Colorways.Primary,
    cta: Colorways.Nickel,
    logo: Colorways.Inverse,
  };
};

const isStackCard = (
  card: HeroBentoCardProps,
): card is Extract<HeroBentoCardProps, { __typename: 'HeroBentoCta' | 'HeroBentoLogo' }> =>
  card.__typename === 'HeroBentoCta' || card.__typename === 'HeroBentoLogo';

const HeroBento = ({
  sys,
  sectionName,
  eyebrowImage,
  eyebrowText,
  headline,
  cards,
  margins,
  colorTheme,
}: HeroBentoProps) => {
  const items = (cards?.items ?? []).filter(Boolean);
  if (items.length === 0) return null;

  const themeColorways = getThemeColorways(colorTheme);

  const stackCards = items.filter(isStackCard);
  const mediaCards = items.filter(
    (card): card is Extract<HeroBentoCardProps, { __typename: 'HeroBentoMedia' }> =>
      card.__typename === 'HeroBentoMedia',
  );

  const renderCard = (card: HeroBentoCardProps) => {
    switch (card.__typename) {
      case 'HeroBentoCta':
        return <HeroBentoCta key={card.sys?.id} {...card} cardColorway={themeColorways.cta} />;
      case 'HeroBentoLogo':
        return <HeroBentoLogo key={card.sys?.id} {...card} cardColorway={themeColorways.logo} />;
      case 'HeroBentoMedia':
        return <HeroBentoMedia key={card.sys?.id} {...card} />;
    }
  };

  return (
    <section {...getSectionProps(themeColorways.section, margins, sectionName)} data-testid="hero-bento">
      <div className="content-wrapper">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          {/* Left column: Eyebrow + Headline */}
          <div className="flex flex-col gap-4 md:max-w-[652px] md:flex-1">
            {eyebrowImage?.url && (
              <div className="relative h-18 w-18 max-md:hidden" {...liveUpdateAttrs(sys?.id, 'eyebrowImage')}>
                <Image
                  src={eyebrowImage.url}
                  alt={eyebrowImage.description || eyebrowImage.title}
                  width={eyebrowImage.width ?? 72}
                  height={eyebrowImage.height ?? 72}
                  className="object-contain"
                />
              </div>
            )}
            {eyebrowText && (
              <div
                className="font-sans text-eyebrow font-semibold tracking-wider"
                {...liveUpdateAttrs(sys?.id, 'eyebrowText')}
                data-testid="hero-bento-eyebrow">
                <RichTextCopy copy={eyebrowText} ySpacing="none" inline />
              </div>
            )}
            <div
              className="font-display text-heading-lg md:text-heading-xxxl tracking-tight"
              {...liveUpdateAttrs(sys?.id, 'headline')}
              data-testid="hero-bento-headline">
              <RichTextHeadline headline={headline} level="h1" />
            </div>
          </div>

          {/* Right column: Cards */}
          <div
            className="flex flex-col gap-2 md:gap-6 md:flex-row md:shrink-0 md:items-stretch"
            {...liveUpdateAttrs(sys?.id, 'cards')}
            data-testid="hero-bento-cards">
            {stackCards.length > 0 && (
              <div className="contents md:flex md:flex-col md:gap-6 md:w-[314px]" data-testid="hero-bento-stack">
                {stackCards.map(card => (
                  <div
                    key={card.sys?.id}
                    className={cn(card.__typename === 'HeroBentoCta' ? 'max-md:order-1' : 'max-md:order-3')}>
                    {renderCard(card)}
                  </div>
                ))}
              </div>
            )}
            {mediaCards.map(card => (
              <div key={card.sys?.id} className="max-md:order-2 md:w-[314px]">
                {renderCard(card)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBento;
